/**
 * What is in the store room.
 *
 * WHAT THIS SCREEN IS ACTUALLY FOR
 * --------------------------------
 * Not "how much do we have" — that is a number anyone can walk in and look at.
 * It is "what do we need to order", and that question needs two things the
 * stock table alone does not hold: the reorder level, and how far below it
 * each item is.
 *
 * So the reorder levels are fetched alongside and joined here. The old screen
 * listed quantities and left the arithmetic to whoever was reading it, which
 * is how a kitchen runs out of chicken on a Friday.
 *
 * THREE STATES, NOT TWO
 * ---------------------
 * Below zero is not the same problem as low, and it is the one worth shouting
 * about. It means more has been consumed than was ever received: an opening
 * balance that was wrong, deliveries never entered, or a recipe that takes
 * more than the dish really uses. Six ingredients are in that state right now.
 *
 * An ingredient with no reorder level set is a third state again — unknown,
 * not healthy. Treating it as zero would quietly reassure, which is worse than
 * saying nothing.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, round2 } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { ExpiryPanel } from './ExpiryPanel.jsx';

export default function StockPage() {
  const [expiryWindow, setExpiryWindow] = useState(7);

  const balances = useQuery({
    queryKey: ['inventory', 'balances'],
    queryFn: () => api.get('/inventory/balances'),
  });

  const ingredients = useQuery({
    queryKey: ['menu', 'ingredients'],
    queryFn: () => api.get('/menu/ingredients'),
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(() => {
    const levels = new Map(
      (ingredients.data?.ingredients ?? []).map((i) => [i.id, i.reorder_level])
    );

    return (balances.data?.balances ?? []).map((row) => {
      const reorder = Number(levels.get(row.ingredient_id)) || 0;
      const quantity = Number(row.quantity) || 0;

      return {
        ...row,
        reorder,
        // Below zero is its own problem, whatever the reorder level says.
        negative: quantity < 0,
        // Only meaningful where a level has been set.
        short: reorder > 0 && quantity >= 0 ? round2(reorder - quantity) : null,
      };
    });
  }, [balances.data, ingredients.data]);

  const impossible = rows.filter((r) => r.negative);
  const needed = rows.filter((r) => r.short > 0);
  const value = round2(rows.reduce((sum, r) => sum + (Number(r.total_value) || 0), 0));

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="font-semibold text-xl text-ink">Stock</h1>
        <div className="flex gap-3 text-sm">
          <span className="text-ink-2">
            Value in store <span className="font-medium text-ink">{formatPKR(value)}</span>
          </span>
          {needed.length > 0 && (
            <span className="rounded-full bg-warn-bg px-3 py-1 font-medium text-warn">
              {needed.length} to reorder
            </span>
          )}
          {impossible.length > 0 && (
            <span className="rounded-full bg-bad-bg px-3 py-1 font-medium text-bad">
              {impossible.length} below zero
            </span>
          )}
        </div>
      </div>

      {impossible.length > 0 && (
        <p className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {impossible.length} item{impossible.length === 1 ? ' has' : 's have'} less than nothing in
          stock. More has been used than was ever received — usually a delivery that was never
          entered, or a recipe that takes more than the dish really does.
        </p>
      )}

      <DataTable
        rows={rows}
        loading={balances.isLoading || ingredients.isLoading}
        error={balances.isError}
        searchable
        searchKeys={['ingredient_name', 'sku']}
        empty="No stock recorded yet."
        rowKey={(row) => row.id}
        columns={[
          {
            key: 'ingredient_name',
            label: 'Item',
            tone: (row) => (row.negative ? 'text-bad' : row.short > 0 ? 'text-warn' : 'text-ink'),
          },
          {
            key: 'quantity',
            label: 'In store',
            align: 'right',
            render: (row) => `${round2(row.quantity)} ${row.unit ?? ''}`.trim(),
            tone: (row) =>
              row.negative ? 'text-bad font-medium' : row.short > 0 ? 'text-warn' : 'text-ink-2',
          },
          {
            key: 'reorder',
            label: 'Reorder at',
            align: 'right',
            render: (row) => (row.reorder > 0 ? round2(row.reorder) : '—'),
          },
          {
            key: 'short',
            label: 'Short by',
            align: 'right',
            // The number that turns a list into a shopping list.
            render: (row) => (row.negative ? 'below zero' : row.short > 0 ? round2(row.short) : ''),
            tone: (row) => (row.negative ? 'text-bad font-medium' : 'text-warn font-medium'),
          },
          {
            key: 'average_cost',
            label: 'Avg cost',
            align: 'right',
            render: (row) => formatPKR(row.average_cost),
          },
          {
            key: 'total_value',
            label: 'Value',
            align: 'right',
            render: (row) => formatPKR(row.total_value),
            tone: () => 'text-ink',
          },
        ]}
      />

      <ExpiryPanel withinDays={expiryWindow} onWindowChange={setExpiryWindow} />
    </div>
  );
}
